<script>
import sizeMixin from "./sizeMixin";
import DashTip from "./DashTip";
import StatusMark from "./StatusMark";
import { clamp } from "./scale";
import { fmtNum, fmtPct, THRESHOLDS } from "@/data/dashboardModel";

// Evolución del avance: área + línea de 2 px con franjas de zona (<60, 60–85, >85) como
// fondo tenue, 4 líneas de cuadrícula (0, 60, 85, 100), crosshair + tooltip (hover y
// teclado ← → Inicio Fin) y último punto anotado. Se dibuja en px reales (sin escalar texto).
export default {
  name: "TrendArea",
  components: { DashTip, StatusMark },
  mixins: [sizeMixin],
  props: {
    history: { type: Array, default: () => [] }, // [{ month, label, longLabel, pct, approvedScore, status }]
    ready: { type: Boolean, default: false },
    requiredScore: { type: Number, default: 0 },
  },
  data() {
    return { idx: -1, focused: false, byKey: false };
  },
  computed: {
    W() {
      return this.width || 440;
    },
    H() {
      return this.W < 480 ? 210 : 240;
    },
    m() {
      return { l: 34, r: 16, t: 14, b: 40 };
    },
    iw() {
      return this.W - this.m.l - this.m.r;
    },
    ih() {
      return this.H - this.m.t - this.m.b;
    },
    n() {
      return this.history.length;
    },
    pts() {
      const n = this.n;
      return this.history.map((h, i) => ({
        x: this.m.l + (n > 1 ? (this.iw * i) / (n - 1) : this.iw / 2),
        y: this.yOf(h.pct),
        h,
        i,
      }));
    },
    liveText() {
      const c = this.current;
      if (!c || !this.focused || !this.byKey) return "";
      const d = this.delta;
      const change = d === null ? "" : `, ${d > 0 ? "+" : ""}${fmtNum(d)} pp frente a ${this.prevOfCurrent.label}`;
      return `${c.h.longLabel}: ${fmtPct(c.h.pct)}, ${fmtNum(c.h.approvedScore)} de ${fmtNum(this.requiredScore)} puntos${change}.`;
    },
    line() {
      return this.pts.map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join("");
    },
    area() {
      if (!this.pts.length) return "";
      const base = this.yOf(0);
      const f = this.pts[0];
      const l = this.pts[this.pts.length - 1];
      return `${this.line}L${l.x.toFixed(1)} ${base}L${f.x.toFixed(1)} ${base}Z`;
    },
    bands() {
      const c = THRESHOLDS.critical;
      const a = THRESHOLDS.acceptable;
      return [
        { key: "good", y0: 100, y1: a, label: "Aceptable" },
        { key: "warning", y0: a, y1: c, label: "Moderado" },
        { key: "critical", y0: c, y1: 0, label: "Crítico" },
      ].map((b) => ({ ...b, y: this.yOf(b.y0), h: this.yOf(b.y1) - this.yOf(b.y0) }));
    },
    ticks() {
      return [0, THRESHOLDS.critical, THRESHOLDS.acceptable, 100].map((v) => ({ v, y: this.yOf(v) }));
    },
    xLabels() {
      const step = this.W < 420 ? 3 : 2;
      return this.pts
        .filter((p) => (this.n - 1 - p.i) % step === 0)
        .map((p) => {
          const [yy, mm] = String(p.h.month).split("-");
          return { x: p.x, label: p.h.label, year: p.i === 0 || mm === "01" || p.i === this.n - 1 ? yy : "" };
        });
    },
    last() {
      return this.pts[this.pts.length - 1];
    },
    current() {
      return this.idx > -1 ? this.pts[this.idx] : null;
    },
    prevOfCurrent() {
      return this.idx > 0 ? this.history[this.idx - 1] : null;
    },
    delta() {
      if (!this.current || !this.prevOfCurrent) return null;
      return Math.round((this.current.h.pct - this.prevOfCurrent.pct) * 100) / 100;
    },
    ariaLabel() {
      const vals = this.history.map((h) => `${h.label} ${fmtNum(h.pct, 1)} %`).join(", ");
      return `Evolución del puntaje ponderado, ${this.n} meses: ${vals}.`;
    },
    endLabelBelow() {
      return this.last && this.last.y < this.m.t + 22;
    },
  },
  methods: {
    fmtNum,
    fmtPct,
    yOf(v) {
      return this.m.t + this.ih * (1 - clamp(v, 0, 100) / 100);
    },
    nearest(clientX) {
      const r = this.$refs.plot.getBoundingClientRect();
      const px = clientX - r.left - this.m.l;
      const t = this.n > 1 ? (px / this.iw) * (this.n - 1) : 0;
      return clamp(Math.round(t), 0, this.n - 1);
    },
    onMove(ev) {
      this.byKey = false;
      this.idx = this.nearest(ev.clientX);
    },
    onLeave() {
      if (!this.focused) this.idx = -1;
    },
    onFocus() {
      this.focused = true;
      this.byKey = true;
      if (this.idx < 0) this.idx = this.n - 1;
    },
    onBlur() {
      this.focused = false;
      this.idx = -1;
    },
    onKey(ev) {
      const k = ev.key;
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(k)) return;
      // No pisar atajos del navegador (Alt+← atrás, Ctrl+Inicio…)
      if (ev.altKey || ev.ctrlKey || ev.metaKey || ev.shiftKey) return;
      ev.preventDefault();
      this.byKey = true;
      const cur = this.idx < 0 ? this.n - 1 : this.idx;
      if (k === "ArrowLeft") this.idx = Math.max(0, cur - 1);
      else if (k === "ArrowRight") this.idx = Math.min(this.n - 1, cur + 1);
      else if (k === "Home") this.idx = 0;
      else this.idx = this.n - 1;
    },
  },
};
</script>

<template>
  <div
    ref="plot"
    class="dsh-trend"
    :class="{ 'is-ready': ready }"
    role="group"
    tabindex="0"
    :aria-label="ariaLabel + ' Usa las flechas izquierda y derecha para recorrer los meses.'"
    @pointermove="onMove"
    @pointerleave="onLeave"
    @focus="onFocus"
    @blur="onBlur"
    @keydown="onKey"
  >
    <svg class="dsh-trend__svg" :width="W" :height="H" :viewBox="`0 0 ${W} ${H}`" aria-hidden="true">
      <g class="dsh-trend__bands">
        <rect v-for="b in bands" :key="b.key" :class="'dsh-trend__band dsh-trend__band--' + b.key" :x="m.l" :y="b.y" :width="iw" :height="b.h" />
        <text v-for="b in bands" :key="b.key + 't'" class="dsh-trend__zone" :x="m.l + 8" :y="b.y + 15">{{ b.label }}</text>
      </g>
      <g class="dsh-trend__grid">
        <line v-for="t in ticks" :key="t.v" :x1="m.l" :x2="W - m.r" :y1="t.y" :y2="t.y" />
        <text v-for="t in ticks" :key="t.v + 'l'" :x="m.l - 8" :y="t.y + 4" text-anchor="end">{{ t.v }}</text>
      </g>
      <g class="dsh-trend__xaxis">
        <template v-for="xl in xLabels">
          <text :key="xl.x + 'm'" :x="xl.x" :y="H - m.b + 17" text-anchor="middle">{{ xl.label }}</text>
          <text v-if="xl.year" :key="xl.x + 'y'" class="dsh-trend__year" :x="xl.x" :y="H - m.b + 31" text-anchor="middle">{{ xl.year }}</text>
        </template>
      </g>
      <path class="dsh-trend__area" :d="area" />
      <path class="dsh-trend__line" :d="line" pathLength="1" />
      <g v-if="current">
        <line class="dsh-trend__cross" :x1="current.x" :x2="current.x" :y1="m.t" :y2="H - m.b" />
        <circle class="dsh-trend__dot" :cx="current.x" :cy="current.y" r="5" />
      </g>
      <g v-if="last" class="dsh-trend__end" :class="{ 'is-quiet': current && current.i !== last.i }">
        <circle class="dsh-trend__dot" :cx="last.x" :cy="last.y" r="5" />
        <text
          class="dsh-trend__endlabel"
          :x="last.x - 10"
          :y="last.y + (endLabelBelow ? 22 : -12)"
          text-anchor="end"
        >{{ fmtPct(last.h.pct) }}</text>
      </g>
    </svg>

    <!-- Anuncia el mes explorado con las flechas (el tooltip visual es aria-hidden) -->
    <p class="sr-only" aria-live="polite" aria-atomic="true">{{ liveText }}</p>

    <DashTip :show="!!current" :x="current ? current.x : 0" :y="current ? current.y : 0">
      <template v-if="current">
        <div class="dsh-tip__head">{{ current.h.longLabel }}</div>
        <div class="dsh-tip__value">{{ fmtPct(current.h.pct) }}</div>
        <div class="dsh-tip__row">{{ fmtNum(current.h.approvedScore) }} de {{ fmtNum(requiredScore) }} puntos aprobados</div>
        <div v-if="delta !== null" class="dsh-tip__row">
          {{ delta > 0 ? "+" : "" }}{{ fmtNum(delta) }} pp frente a {{ prevOfCurrent.label }}
        </div>
        <div class="dsh-tip__status"><StatusMark :status="current.h.status" /></div>
      </template>
    </DashTip>
  </div>
</template>
